import type { ComponentType } from "react";
import {
    AddressIcon,
    BankIcon,
    CalendarIcon,
    CancelledDocumentIcon,
    CarDocumentIcon,
    CarIcon,
    CoinsEuroIcon,
    DocumentXIcon,
    House2Icon,
    HouseIcon,
    Money2Icon,
    MoneyIcon,
    MoneyTransferIcon,
    PinIcon,
    ProfileIcon,
} from "vig-ui-library";
import type { IconProps } from "vig-ui-library";

/**
 * ReactIcon values from API (RequestTypes / RequestTags), plus legacy Material icon names
 * still used when reactIcon is absent.
 */
export type PKPIconKey =
    | "money"
    | "car"
    | "profile"
    | "calendar"
    | "money_2"
    | "bank"
    | "address"
    | "house"
    | "house_2"
    | "coins_euro"
    | "car_document"
    | "cancelled_document"
    | "money_transfer"
    | "document_x"
    | "pin"
    | "published_with_changes"
    | "history"
    | "euro_symbol"
    | "directions_car"
    | "contact_page"
    | "home"
    | "person_search"
    | "timelapse";

const ICON_MAP: Record<PKPIconKey, ComponentType<IconProps>> = {
    money: MoneyIcon,
    car: CarIcon,
    profile: ProfileIcon,
    calendar: CalendarIcon,
    money_2: Money2Icon,
    bank: BankIcon,
    address: AddressIcon,
    house: HouseIcon,
    house_2: House2Icon,
    coins_euro: CoinsEuroIcon,
    car_document: CarDocumentIcon,
    cancelled_document: CancelledDocumentIcon,
    money_transfer: MoneyTransferIcon,
    document_x: DocumentXIcon,
    pin: PinIcon,
    published_with_changes: CarIcon,
    history: DocumentXIcon,
    euro_symbol: MoneyIcon,
    directions_car: CarIcon,
    contact_page: ProfileIcon,
    home: PinIcon,
    person_search: ProfileIcon,
    timelapse: Money2Icon,
};

export type PKPIconSwitcherProps = IconProps & {
    /** Key from API (reactIcon) or legacy icon name. */
    iconKey: PKPIconKey | string | null | undefined;
    /** Rendered when iconKey is missing/empty. Unknown keys still render nothing. */
    fallbackIcon?: ComponentType<IconProps>;
};

/**
 * Renders a PKP icon for the given key. Accepts width, height, and color.
 * Unknown keys render an empty fragment. When iconKey is empty, optional fallbackIcon is used.
 */
export const PKPIconSwitcher = ({
    width = 48,
    height = 48,
    color = "#C40109",
    iconKey,
    fallbackIcon: FallbackIcon,
}: PKPIconSwitcherProps) => {
    const key = typeof iconKey === "string" ? iconKey.trim() : "";

    if (!key) {
        if (!FallbackIcon) {
            return <></>;
        }
        return (
            <FallbackIcon
                width={width}
                height={height}
                color={color}
            />
        );
    }

    const IconComponent = ICON_MAP[key as PKPIconKey];
    if (!IconComponent) {
        return <></>;
    }

    return (
        <IconComponent
            width={width}
            height={height}
            color={color}
        />
    );
};
